import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, BookOpen, Target, Users, Clock } from 'lucide-react';

const GrammarPage = () => {
  const navigate = useNavigate();
  const [selectedLevel, setSelectedLevel] = useState('A1');

  const grammarTopics = {
    A1: [
      { title: 'Der, die, das - Artikel', progress: 100, lessons: 8 },
      { title: 'Präsens - Gegenwart', progress: 100, lessons: 12 },
      { title: 'Akkusativ - Wen oder was?', progress: 80, lessons: 10 },
      { title: 'Modalverben - können, müssen, wollen', progress: 60, lessons: 15 },
      { title: 'Zahlen und Zeit', progress: 90, lessons: 6 }
    ],
    A2: [
      { title: 'Perfekt - Vergangenheit', progress: 70, lessons: 14 },
      { title: 'Dativ - Wem?', progress: 85, lessons: 12 },
      { title: 'Wechselpräpositionen', progress: 50, lessons: 16 },
      { title: 'Komparativ und Superlativ', progress: 40, lessons: 8 },
      { title: 'Reflexive Verben', progress: 30, lessons: 10 }
    ],
    B1: [
      { title: 'Konjunktiv II - Höflichkeit', progress: 25, lessons: 18 },
      { title: 'Passiv - Vorgangspassiv', progress: 10, lessons: 20 },
      { title: 'Nebensätze mit dass, weil, obwohl', progress: 60, lessons: 22 },
      { title: 'Partizip I und II', progress: 0, lessons: 12 },
      { title: 'Indirekte Rede', progress: 0, lessons: 15 }
    ]
  };

  const levels = [
    { code: 'A1', name: 'Anfänger', color: 'bg-green-500' },
    { code: 'A2', name: 'Grundkenntnisse', color: 'bg-green-600' },
    { code: 'B1', name: 'Mittelstufe', color: 'bg-yellow-500' },
    { code: 'B2', name: 'Selbstständig', color: 'bg-yellow-600' },
    { code: 'C1', name: 'Fortgeschritten', color: 'bg-red-500' },
    { code: 'C2', name: 'Fachkundig', color: 'bg-red-600' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Button variant="ghost" onClick={() => navigate('/')} className="mr-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Zurück
              </Button>
              <BookOpen className="h-8 w-8 text-[#4ECDC4] mr-2" />
              <span className="text-2xl font-bold text-slate-900">DMV-Lernportal</span>
            </div>
            <Button onClick={() => navigate('/auth')} className="bg-[#4ECDC4] hover:bg-[#45b7aa]">
              Anmelden
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Deutsche Grammatik lernen
          </h1>
          <p className="text-xl text-slate-600 mb-8">
            Strukturiert von A1 bis C2 - mit interaktiven Übungen und Erklärungen
          </p>
          
          {/* Level Selection */}
          <div className="flex justify-center space-x-2 mb-8">
            {levels.map((level) => (
              <Button
                key={level.code}
                variant={selectedLevel === level.code ? "default" : "outline"}
                onClick={() => setSelectedLevel(level.code)}
                className={selectedLevel === level.code ? `${level.color} text-white` : ''}
              >
                {level.code}
              </Button>
            ))}
          </div>
        </div>

        {/* Grammar Topics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900">
                Grammatik Level {selectedLevel}
              </h2>
              <Badge className={`text-white px-4 py-1 ${levels.find(l => l.code === selectedLevel)?.color}`}>
                {levels.find(l => l.code === selectedLevel)?.name}
              </Badge>
            </div>

            <div className="space-y-4">
              {grammarTopics[selectedLevel]?.map((topic, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-slate-900">{topic.title}</h3>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-slate-600">{topic.lessons} Lektionen</span>
                        <Badge variant={topic.progress === 100 ? "default" : topic.progress > 0 ? "secondary" : "outline"}>
                          {topic.progress === 100 ? "✓ Abgeschlossen" : topic.progress > 0 ? `${topic.progress}%` : "Neu"}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                      <div 
                        className={`h-2 rounded-full transition-all ${levels.find(l => l.code === selectedLevel)?.color}`}
                        style={{ width: `${topic.progress}%` }}
                      ></div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm text-slate-600">
                      <span>{Math.round(topic.lessons * topic.progress / 100)} von {topic.lessons} Lektionen</span>
                      <span>{topic.progress > 0 ? `${Math.round(topic.lessons * 3 * (100 - topic.progress) / 100)} Min übrig` : `~${topic.lessons * 3} Min`}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-8 text-center">
              <Button 
                onClick={() => navigate('/auth')}
                className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white px-8 py-3"
              >
                Grammatik-Training starten
              </Button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Progress Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Target className="h-5 w-5 mr-2" />
                  Dein Fortschritt
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {levels.slice(0, 3).map((level) => {
                  const topics = grammarTopics[level.code] || [];
                  const avgProgress = topics.length > 0 
                    ? Math.round(topics.reduce((sum, t) => sum + t.progress, 0) / topics.length)
                    : 0;
                  
                  return (
                    <div key={level.code} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${level.color} mr-2`}></div>
                        <span className="text-sm font-medium">{level.code}</span>
                      </div>
                      <span className="text-sm text-slate-600">{avgProgress}%</span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Study Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  Lerntipps
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-slate-600">
                <p>• Lerne jeden Tag 15-20 Minuten</p>
                <p>• Wiederhole schwierige Themen</p>
                <p>• Kombiniere Grammatik mit Stories</p>
                <p>• Nutze die Merksätze und Beispiele</p>
              </CardContent>
            </Card>

            {/* Community */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="h-5 w-5 mr-2" />
                  Community
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span>Aktive Lerner</span>
                  <span className="font-bold text-[#4ECDC4]">1,234</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Heute online</span>
                  <span className="font-bold text-green-500">456</span>
                </div>
                <Button variant="outline" size="sm" className="w-full mt-2">
                  Forum beitreten
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrammarPage;